import {
  amorphicStatic,
  property,
  remote,
  supertypeClass,
  Remoteable,
  Persistable,
  Supertype,
} from '@haventech/amorphic';
import request from 'request-promise-native';
import { CommonUtils } from '../../js/static/CommonUtils';
import { Utils } from '../../js/Utils';
import { serverOnly } from '../helpers';
import { DocuSignTabsResponse } from '@hit/hit-api-client';
import _ from 'lodash';
import { DocuSignWebClient } from '../wsclient/DocuSignWebClient';
import { LaunchDarklyInstance, LD_APP } from '../launch-darkly/LaunchDarklySingleton';
import { FeatureFlags } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';

interface DocuSignTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

interface DocuSignToken {
  accessToken: string;
  expirationDate: number;
}

@supertypeClass({ toClient: true, toServer: true })
export class ESignSessionMetadata extends Remoteable(Persistable(Supertype)) {
  @property({ comment: 'When the documents relating to this signature have been generated' })
  docsGeneratedAt: Date;

  @property({ comment: 'A string representing an error on generation or link retrieval' })
  error: string;

  @property({ comment: 'EnvelopeId for this esign link' })
  envelopeId: string;

  @property({ comment: 'When the documents relating to this signature have been retrieved or saved/persisted' })
  docsRetrievedAt: Date;

  @property({ comment: 'Used to determine docusign stage message' })
  ownerSignComplete: boolean = false;

  @property({ comment: 'Used to determine docusign stage message' })
  waitingOnOtherSigners: boolean = false;

  @property({ comment: 'Used in client account center when the insured has not yet signed, for owner != insured' })
  waitingOnInsured: boolean = false;

  @property({ comment: 'Used in client account center when a joint owner has not yet signed' })
  waitingOnSecondOwner: boolean = false;

  @property({ comment: 'The link or hash for this signature, i.e. a url or a hash' })
  linkValue: string;

  @property({
    comment:
      'When this link has been generated, note the linkValue and linkGeneratedAt can be set at different times than the docs',
  })
  linkGeneratedAt: Date;

  @property({ comment: 'How many attempts have occured when attempting to sign in to do this signature' })
  validationAttempts: number;

  static new(): ESignSessionMetadata {
    return new ESignSessionMetadata();
  }

  @remote({ on: 'server' })
  async setOwnerSignComplete() {
    if (this.ownerSignComplete && !this.waitingOnOtherSigners) {
      // Don't make unnecessary requests
      return;
    }
    const headers = { 'Content-Type': 'application/json' };
    const webServiceName = 'SendApplicationToDocuSign';
    const svcConfig = amorphicStatic.config.configStore.webservice.get('wsclients')[webServiceName];
    await ESignSessionMetadata.getHeaders(svcConfig, webServiceName, headers);
    const options = {
      url: `${svcConfig.liveEndpointUrl}${svcConfig.accountKey}/envelopes/${this.envelopeId}/recipients/`,
      method: 'GET',
      headers: headers,
      json: true,
    };

    const envRecipients = await request.get(options);
    if (!envRecipients || !envRecipients.signers) {
      return;
    }

    let recipient = envRecipients.signers.find((signer) => signer.roleName === 'Owner');
    if (!recipient) {
      // if no Owner then Owner is Insured
      recipient = envRecipients.signers.find((signer) => signer.roleName === 'Insured');
    }
    if (recipient && recipient.status === 'completed') {
      this.ownerSignComplete = true;
    }

    envRecipients.signers.forEach((signer) => {
      if (signer !== recipient) {
        if (signer.roleName === 'Insured') {
          this.waitingOnInsured = signer.status !== 'completed';
        }
        if (signer.roleName === 'SecondOwner') {
          this.waitingOnSecondOwner = signer.status !== 'completed';
        }
      }
    });

    this.waitingOnOtherSigners = this.waitingOnInsured || this.waitingOnSecondOwner;
  }

  @serverOnly()
  async setEmbedUrl(email: string, fullName: string, appNumber: string, ignoreDocusignPacketError?: boolean) {
    if (!this.docsGeneratedAt) {
      this.linkValue = null;
      this.linkGeneratedAt = null;
      return;
    }
    const headers = { 'Content-Type': 'application/json' };
    const webServiceName = 'SendApplicationToDocuSign';
    // this is hard coded here because there is a circular dependency when you use WebServiceUtils
    const svcConfig = amorphicStatic.config.configStore.webservice.get('wsclients')[webServiceName];
    await ESignSessionMetadata.getHeaders(svcConfig, webServiceName, headers);
    const baseUrl = this.amorphic.config.nconf.get('baseCoverpathClientUrl');
    let returnUrl = `${baseUrl}/signature-redirect/application/${appNumber}/`;
    returnUrl = Utils.convertUrlForDevBoxes(returnUrl); // automatically does nothing if not applicable

    const options = {
      url: `${svcConfig.liveEndpointUrl}${svcConfig.accountKey}/envelopes/${this.envelopeId}/views/recipient/`,
      method: 'POST',
      headers: headers,
      body: {
        returnUrl: returnUrl,
        authenticationMethod: 'None',
        email: email,
        userName: fullName,
        clientUserId: CommonUtils.DOCUSIGN_REDIRECT_CLIENT_USER_ID,
      },
      json: true,
    };
    try {
      const jsonResponse = await request.post(options);
      if (jsonResponse && jsonResponse.url) {
        this.linkValue = jsonResponse.url;
        this.linkGeneratedAt = Utils.now();
      } else {
        LoggerSingleton.getInstance().error(
          {
            module: 'ESignSessionMetadata',
            function: 'setEmbedUrl',

            category: Category.request,
            error: {
              isHumanRelated: false,
            },
          },
          'Error in fetching links or signer status from docusign'
        );
        this.error = 'Error url not generated: Url is blank';
      }
    } catch (e) {
      if (!ignoreDocusignPacketError) {
        let message;
        if (e && e.getMessage) {
          message = 'Error url not generated:' + e.getMessage();
        } else if (e && e.error && e.error.errorCode) {
          message = e.error.errorCode;
        } else {
          message = 'Error url not generated:unknown error';
        }
        LoggerSingleton.getInstance().error(
          {
            module: 'ESignSessionMetadata',
            function: 'setEmbedUrl',
            category: Category.request,

            error: {
              name: e?.name,
              message: message,
              stack: e?.stack,
              isHumanRelated: false,
            },
          },
          'Error in fetching links or signer status from docusign'
        );
        this.error = message;
      }
    }
  }

  async voidEnvelope(): Promise<void> {
    const headers = { 'Content-Type': 'application/json' };
    const webServiceName = 'SendApplicationToDocuSign';
    const svcConfig = amorphicStatic.config.configStore.webservice.get('wsclients')[webServiceName];
    await ESignSessionMetadata.getHeaders(svcConfig, webServiceName, headers);

    const options = {
      url: `${svcConfig.liveEndpointUrl}${svcConfig.accountKey}/envelopes/${this.envelopeId}`,
      method: 'POST',
      headers: headers,
      body: {
        status: 'voided',
        voidedReason: 'A New Envelope Was Requested',
      },
      json: true,
    };

    try {
      const jsonResponse = await request.put(options);

      if (jsonResponse.status === 200) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'ESignSessionMetadata',
            function: 'voidEnvelope',
            category: UICategory.request,
          },
          `Envelope voided with id ${this.envelopeId}`
        );
      } else {
        UILoggerSingleton.getInstance().error(
          {
            module: 'ESignSessionMetadata',
            function: 'voidEnvelope',
            category: UICategory.request,
            error: {
              isHumanRelated: false,
            },
          },
          `failed to void envelope with id ${this.envelopeId}`
        );
      }
    } catch (err) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'ESignSessionMetadata',
          function: 'voidEnvelope',
          category: UICategory.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
          },
        },
        `failed to void envelope with id ${this.envelopeId}`
      );
    }
  }

  static async getDocuSignTabs(forms, envelopeId): Promise<DocuSignTabsResponse> {
    const formNameMap = {
      FR2200: 'fr2200 (2).pdf',
      FR3010: 'fr3010 (1).pdf',
    };
    const headers = { 'Content-Type': 'application/json' };
    const webServiceName = 'RetrieveSignedDocuments';
    const svcConfig = amorphicStatic.config.configStore.webservice.get('wsclients')[webServiceName];
    await this.getHeaders(svcConfig, webServiceName, headers);
    const options = {
      url: `${svcConfig.liveEndpointUrl}${svcConfig.accountKey}/envelopes/${envelopeId}/documents/`,
      method: 'GET',
      headers: headers,
      json: true,
    };

    let tabs = [];
    try {
      const docuSignAllDocs = await request.get(options);
      const requestedDocuments = (docuSignAllDocs.envelopeDocuments || []).filter((envelopeDocument) =>
        forms.find((form) => formNameMap[form] === envelopeDocument.name)
      );
      if (requestedDocuments.length) {
        tabs = await Promise.all(
          requestedDocuments.map(async (doc) => {
            const options = {
              url: `${svcConfig.liveEndpointUrl}${svcConfig.accountKey}${doc.uri}/tabs`,
              method: 'GET',
              headers,
              json: true,
            };

            return {
              name: _.invert(formNameMap)[doc.name],
              ...(await request.get(options)),
            };
          })
        );
      }
    } catch (err) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'ESignSessionMetadata',
          function: 'getDocuSignTabs',
          category: UICategory.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
          },
        },
        'Failed to fetch tabs from DocuSign:'
      );
    }

    return {
      forms: tabs,
    } as DocuSignTabsResponse;
  }

  static async getHeaders(svcConfig, webServiceName, headers) {
    const docuSignWebClient = new DocuSignWebClient(webServiceName);
    const isDocuSignOAuthEnabled = await this.getIsDocuSignOAuthEnabled(webServiceName);
    _.extend(headers, { ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.() });

    if (isDocuSignOAuthEnabled) {
      // CurrentTime + 10 minutes to add some buffer for refresh
      const currTime = Math.floor(Date.now() / 1000) + 600;

      if (!DocuSignWebClient.token || (DocuSignWebClient.token && DocuSignWebClient.token.expirationDate <= currTime)) {
        DocuSignWebClient.token = await docuSignWebClient.getAccessToken(svcConfig);
      }

      headers['Authorization'] = `Bearer ` + DocuSignWebClient.token.accessToken;

      return;
    }

    headers['X-DocuSign-Authentication'] = JSON.stringify({
      Username: svcConfig.wsUsername,
      Password: svcConfig.wsPassword,
      IntegratorKey: svcConfig.integratorKey,
    });

    return;
  }

  @serverOnly()
  static async getIsDocuSignOAuthEnabled(userId: string): Promise<boolean> {
    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    return ldInstance.getFeatureFlagValueAsBoolean(FeatureFlags.isDocuSignOAuthEnabled, userId);
  }
}
